import { CircularProgress, SvgIcon } from '@material-ui/core';
import { createMuiTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import {
    ReachCheckboxCheckedIcon,
    ReachCheckboxIcon,
    ReachCheckboxIndeterminateIcon,
    ReachRadioCheckedIcon,
    ReachRadioIcon,
} from '@penji/admin/components/pattern-library/icons/reach-control-icons';
import { ReactComponent as ReachCaretDownBoldIcon } from '@penji/admin/components/pattern-library/icons/reach/caret-down-bold.svg';
import { ReactComponent as ReachCaretLeftBoldIcon } from '@penji/admin/components/pattern-library/icons/reach/caret-left-bold.svg';
import { ReactComponent as ReachCaretRightBoldIcon } from '@penji/admin/components/pattern-library/icons/reach/caret-right-bold.svg';
import { ReactComponent as ReachCloseIcon } from '@penji/admin/components/pattern-library/icons/reach/close.svg';
import { ReactComponent as ReachErrorIcon } from '@penji/admin/components/pattern-library/icons/reach/error.svg';
import { ReactComponent as ReachInfoIcon } from '@penji/admin/components/pattern-library/icons/reach/info.svg';
import { ReactComponent as ReachWarningIcon } from '@penji/admin/components/pattern-library/icons/reach/warning.svg';
import { Brand } from '@penji/frontend/brand';
import { colors, reach } from '@penji/style-guide';
import React, { Suspense, lazy } from 'react';
import { StyleSheet, View } from 'react-native';

const LazyApp = lazy(() => import('@penji/admin/components/app'));

export const AppLoader = () => {
    return (
        <ThemeProvider theme={theme}>
            <Suspense
                fallback={
                    <View style={styles.progressContainer}>
                        <CircularProgress />
                    </View>
                }
            >
                <LazyApp />
            </Suspense>
        </ThemeProvider>
    );
};

const styles = StyleSheet.create({
    progressContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});

const getReachButtonVariantOverride = ({
    color,
    backgroundColor,
    border,
    boxShadow,
    hoverBackgroundColor,
    hoverBorder,
    iconColor,
}) => {
    return {
        ...(!color ? null : { color }),
        ...(!backgroundColor ? null : { backgroundColor }),
        ...(!border ? null : { border }),
        ...(!boxShadow ? null : { boxShadow }),
        ...(!hoverBackgroundColor && !hoverBorder && !boxShadow
            ? null
            : {
                  '&:hover': {
                      ...(!hoverBackgroundColor
                          ? null
                          : { backgroundColor: hoverBackgroundColor }),
                      ...(!hoverBorder ? null : { border: hoverBorder }),
                      ...(!boxShadow ? null : { boxShadow }),
                  },
              }),
        ...(!boxShadow
            ? null
            : {
                  '&:active': { boxShadow },
                  '&$focusVisible': { boxShadow },
              }),
        '&$disabled': {
            ...(!color ? null : { color }),
            ...(!backgroundColor ? null : { backgroundColor }),
            ...(!border ? null : { border }),
            ...(!boxShadow ? null : { boxShadow }),
        },
        ...(!iconColor
            ? null
            : { '& $startIcon, & $endIcon': { color: iconColor } }),
    };
};

const getReachIconButtonOverrides = () => {
    return {
        root: {
            color: reach.colors['stone-600'],
            borderRadius: 6,
            '&:focus-visible': {
                outline: `2px solid ${reach.colors['sky-700']}`,
                outlineOffset: 1,
            },
            '&:hover': {
                backgroundColor: reach.colors['stone-200'],
            },
            '&$disabled': {
                opacity: 0.5,
                color: reach.colors['stone-600'],
            },
        },

        sizeSmall: {
            '& svg': {
                width: 16,
                height: 16,
            },
            padding: 6,
        },

        colorPrimary: getReachButtonVariantOverride({
            color: reach.colors['sky-900'],
            hoverBackgroundColor: reach.colors['sky-50'],
        }),

        colorSecondary: getReachButtonVariantOverride({
            color: reach.colors['red-400'],
            hoverBackgroundColor: reach.colors['red-50'],
        }),
    };
};

const getReachButtonOverrides = () => {
    return {
        root: {
            ...reach.textStyles['sm-medium'],
            padding: '8px 16px',
            borderRadius: 6,
            textTransform: 'none',
            height: 40,
            '&$disabled': {
                opacity: 0.5,
                color: reach.colors['stone-950'],
            },
            '& $label': {
                gap: '6px',
            },
            '& $startIcon, & $endIcon': {
                margin: 0,
                '& svg': {
                    width: 16,
                    height: 16,
                },
            },
            '&:hover': {
                backgroundColor: reach.colors['stone-100'],
            },
            // With ripple disabled, buttons need a keyboard focus indicator
            '&:focus-visible': {
                outline: `2px solid ${reach.colors['sky-700']}`,
                outlineOffset: 1,
            },
        },

        sizeSmall: {
            ...reach.textStyles['sm-medium'],
            height: 32,
            padding: '8px 12px',
        },

        // Reach has no large button yet, neutralize MUI's per-variant sizeLarge
        // rules so size="large" renders identical to medium.
        sizeLarge: {
            ...reach.textStyles['sm-medium'],
            padding: '8px 16px',
            height: 40,
        },

        text: {
            padding: '8px 16px',
            ...getReachButtonVariantOverride({
                color: reach.colors['stone-950'],
            }),
        },

        textPrimary: getReachButtonVariantOverride({
            color: reach.colors['sky-900'],
            hoverBackgroundColor: reach.colors['sky-50'],
        }),

        textSecondary: getReachButtonVariantOverride({
            color: reach.colors['red-400'],
            hoverBackgroundColor: reach.colors['red-50'],
        }),

        outlined: getReachButtonVariantOverride({
            color: reach.colors['stone-950'],
            border: `1px solid ${reach.colors['medium-gray-300']}`,
            boxShadow: reach.shadows.xs,
            hoverBackgroundColor: reach.colors['stone-100'],
            iconColor: reach.colors['stone-950'],
        }),

        outlinedPrimary: getReachButtonVariantOverride({
            color: reach.colors['sky-900'],
            border: `1px solid ${reach.colors['sky-700']}`,
            hoverBackgroundColor: reach.colors['sky-50'],
            hoverBorder: `1px solid ${reach.colors['sky-900']}`,
            iconColor: 'inherit',
        }),

        outlinedSecondary: getReachButtonVariantOverride({
            color: reach.colors['red-400'],
            border: `1px solid ${reach.colors['red-300']}`,
            hoverBackgroundColor: reach.colors['red-50'],
            hoverBorder: `1px solid ${reach.colors['red-400']}`,
            iconColor: 'inherit',
        }),

        contained: getReachButtonVariantOverride({
            color: reach.colors['stone-950'],
            backgroundColor: reach.colors['stone-50'],
            border: `1px solid ${reach.colors['medium-gray-300']}`,
            boxShadow: reach.shadows.xs,
            hoverBackgroundColor: reach.colors['stone-100'],
            iconColor: reach.colors['stone-950'],
        }),

        containedPrimary: getReachButtonVariantOverride({
            color: reach.colors['white'],
            backgroundColor: reach.colors['sky-900'],
            boxShadow: reach.shadows.xs,
            border: 'none',
            hoverBackgroundColor: reach.colors['sky-950'],
            iconColor: reach.colors['white'],
        }),

        containedSecondary: getReachButtonVariantOverride({
            color: reach.colors['white'],
            backgroundColor: reach.colors['red-400'],
            boxShadow: reach.shadows.xs,
            border: 'none',
            hoverBackgroundColor: reach.colors['red-410'],
            iconColor: reach.colors['white'],
        }),
    };
};

const getReachOutlinedInputOverrides = () => {
    return {
        root: {
            ...reach.textStyles['base-regular'],
            color: reach.colors['stone-950'],
            backgroundColor: reach.colors['stone-50'],
            borderRadius: 6,
            boxShadow: reach.shadows.xs,

            'label + &': {
                marginTop: 8,
            },
            '& $notchedOutline': {
                borderColor: reach.colors['medium-gray-400'],
            },

            '&:hover $notchedOutline': {
                borderColor: reach.colors['medium-gray-400'],
            },

            '&$focused $notchedOutline': {
                borderWidth: 1,
                borderColor: reach.colors['medium-gray-400'],
            },
            '&$focused': {
                boxShadow: `0 0 0 3px ${reach.colors['medium-gray-300']}, ${reach.shadows.xs}`,
            },
            '&$disabled': {
                color: reach.colors['stone-500'],
                boxShadow: 'none',
            },
            '&$disabled $notchedOutline': {
                borderColor: reach.colors['stone-300'],
            },
        },

        input: {
            height: 24,
            padding: '8px 12px',
            '&::placeholder': {
                color: reach.colors['medium-gray-500'],
                opacity: 1,
            },
        },

        inputMarginDense: {
            paddingTop: 4,
            paddingBottom: 4,
        },

        multiline: {
            padding: '8px 12px',
        },

        inputMultiline: {
            height: 'auto',
        },
    };
};

const getReachSelectOverrides = () => {
    return {
        select: {
            '&:focus': {
                backgroundColor: 'transparent',
            },
        },

        icon: {
            fontSize: 12,
            top: 'calc(50% - 6px)',
            marginRight: 4,
            color: reach.colors['stone-600'],
            '&$disabled': {
                color: reach.colors['stone-500'],
            },
        },
    };
};

const getReachAlertVariantOverride = ({
    color,
    backgroundColor,
    iconColor,
}) => {
    return {
        color,
        backgroundColor,
        '& $icon': {
            color: iconColor,
        },
    };
};

const getReachAutocompleteOverrides = () => {
    return {
        inputRoot: {
            '&[class*="MuiOutlinedInput-root"]': {
                padding: 0,
                '& $input': {
                    padding: '8px 4px',
                },
                '& $input:first-child': {
                    paddingLeft: 12,
                },
            },
        },

        endAdornment: {
            top: '50%',
            transform: 'translateY(-50%)',
        },

        popupIndicator: {
            padding: 6,
            '& svg': {
                width: 12,
                height: 12,
            },
        },

        clearIndicator: {
            padding: 4,
            '& svg': {
                width: 14,
                height: 14,
            },
        },
    };
};

const getReachControlOverrides = () => {
    const controlStyle = {
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&$checked': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '&$disabled': {
            opacity: 0.5,
        },
    };

    return {
        root: controlStyle,
        colorPrimary: controlStyle,
        colorSecondary: controlStyle,
    };
};

const getReachFormControlLabelOverrides = () => {
    return {
        label: {
            ...reach.textStyles['sm-regular'],
            color: reach.colors['stone-950'],
            '&$disabled': {
                color: reach.colors['stone-500'],
            },
        },
    };
};

const getReachFormLabelOverrides = () => {
    return {
        root: {
            ...reach.textStyles['sm-medium'],
            color: reach.colors['stone-600'],
            '&$focused': {
                color: reach.colors['stone-600'],
            },
            '&$disabled': {
                color: reach.colors['stone-500'],
            },
        },
    };
};

const getReachInputLabelOverrides = () => {
    return {
        outlined: {
            ...reach.textStyles['base-regular'],
            transform: 'translate(12px, 16px)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '&$marginDense': {
                ...reach.textStyles['sm-regular'],
                transform: 'translate(12px, 14px)',
            },
            '&$shrink': {
                transform: 'translate(8px, 1px) scale(0.8)',
                backgroundColor: reach.colors['stone-50'],
                padding: '0 8px',
            },
        },
    };
};

const getReachFormHelperTextOverrides = () => {
    return {
        root: {
            ...reach.textStyles['sm-regular'],
            color: reach.colors['stone-700'],
        },

        contained: {
            marginLeft: 12,
            marginRight: 12,
        },
    };
};

const getReachChipOverrides = () => ({
    root: {
        ...reach.textStyles['sm-medium'],
        color: reach.colors['stone-950'],
        backgroundColor: reach.colors['light-gray-200'],
        '&:focus-visible': {
            outline: `2px solid ${reach.colors['sky-700']}`,
            outlineOffset: 1,
        },
    },
    clickable: {
        '&:hover, &:focus': {
            backgroundColor: reach.colors['stone-300'],
        },
    },

    icon: {
        color: 'inherit',
    },
});

const getReachTooltipOverrides = () => ({
    tooltip: {
        ...reach.textStyles['sm-regular'],
        color: reach.colors['stone-50'],
        backgroundColor: reach.colors['stone-950'],
        borderRadius: 6,
        padding: '8px 12px',
    },
    arrow: {
        color: reach.colors['stone-950'],
    },
});

const getReachDialogOverrides = () => ({
    paper: {
        border: `1px solid ${reach.colors['stone-300']}`,
        borderRadius: 8,
        boxShadow: reach.shadows.lg,
    },
});

const getReachPopoverOverrides = () => ({
    paper: {
        color: reach.colors['stone-950'],
        border: `1px solid ${reach.colors['stone-300']}`,
        borderRadius: 6,
        boxShadow: reach.shadows.md,
    },
});

const getReachAlertOverrides = () => {
    return {
        root: {
            borderRadius: 12,
        },
        icon: {
            '& svg': {
                width: 20,
                height: 20,
            },
        },

        standardError: getReachAlertVariantOverride({
            color: reach.colors['red-900'],
            backgroundColor: reach.colors['red-50'],
            iconColor: reach.colors['red-400'],
        }),

        standardInfo: getReachAlertVariantOverride({
            color: reach.colors['sky-900'],
            backgroundColor: reach.colors['sky-50'],
            iconColor: reach.colors['sky-600'],
        }),

        standardWarning: getReachAlertVariantOverride({
            color: reach.colors['yellow-900'],
            backgroundColor: reach.colors['yellow-50'],
            iconColor: reach.colors['yellow-700'],
        }),
    };
};

export const getReachCalendarArrowIconProps = () => {
    return {
        leftArrowIcon: <SvgIcon component={ReachCaretLeftBoldIcon} />,
        rightArrowIcon: <SvgIcon component={ReachCaretRightBoldIcon} />,
        leftArrowButtonProps: {
            size: 'small',
            style: { margin: 3, padding: 14 },
        },
        rightArrowButtonProps: {
            size: 'small',
            style: { margin: 3, padding: 14 },
        },
    };
};

const getReachButtonGroupOverrides = () => {
    return {
        contained: {
            boxShadow: reach.shadows.xs,
        },
        groupedContainedHorizontal: {
            '&:not(:last-child)': {
                borderRight: `1px solid ${reach.colors['medium-gray-300']}`,
                '&$disabled': {
                    borderRight: `1px solid ${reach.colors['medium-gray-300']}`,
                },
            },
        },
    };
};

const getReachPaperOverrides = () => {
    return {
        outlined: {
            borderRadius: 12,
            border: `1px solid ${reach.colors['stone-300']}`,
            backgroundColor: reach.colors.white,
            boxShadow: reach.shadows.sm,
        },
    };
};

const getReachAccordionOverrides = () => {
    return {
        root: {
            border: `1px solid ${reach.colors['stone-300']}`,
            boxShadow: reach.shadows.xs,
            '&:before': {
                display: 'none',
            },
            '& + &': {
                borderTop: 'none',
            },
            '&$expanded': {
                margin: 0,
            },
        },
        rounded: {
            '&:first-child': {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
            },
            '&:last-child': {
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
            },
        },
    };
};

const getReachAccordionSummaryOverrides = () => {
    return {
        root: {
            '&$focused': {
                backgroundColor: reach.colors['stone-200'],
            },
        },
        expandIcon: {
            color: reach.colors['stone-950'],
            '& svg': {
                width: 16,
                height: 16,
            },
        },
    };
};

const getReachMenuOverrides = () => {
    return {
        paper: {
            boxShadow: reach.shadows.md,
        },
    };
};

const getReachDividerOverrides = () => {
    return {
        root: {
            backgroundColor: reach.colors['stone-300'],
        },
    };
};

const getReachMenuItemOverrides = () => {
    return {
        root: {
            ...reach.textStyles['sm-regular'],
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 12,
            paddingRight: 12,
            '&:hover': {
                backgroundColor: reach.colors['light-gray-100'],
            },
        },
    };
};

const getReachTableCellOverrides = () => {
    return {
        root: {
            ...reach.textStyles['sm-regular'],
            borderBottom: `1px solid ${reach.colors['stone-300']}`,
        },
        head: {
            ...reach.textStyles['sm-medium'],
        },
        stickyHeader: {
            backgroundColor: reach.colors['stone-100'],
        },
    };
};

const getReachTableRowOverrides = () => {
    return {
        root: {
            '&$hover:hover': {
                backgroundColor: reach.colors['light-gray-100'],
            },
        },
    };
};

const getReachTypography = () => {
    return {
        fontFamily: reach.fontFamily,

        h3: reach.textStyles['5xl-regular'],
        h4: reach.textStyles['3xl-regular'],
        h5: reach.textStyles['2xl-regular'],
        h6: reach.textStyles['xl-medium'],

        subtitle1: reach.textStyles['base-medium'],
        subtitle2: reach.textStyles['sm-medium'],

        body1: reach.textStyles['base-regular'],
        body2: reach.textStyles['sm-regular'],

        caption: reach.textStyles['xs-regular'],
    };
};

const theme = createMuiTheme({
    palette: {
        primary: {
            main: Brand.select({
                penji: colors['blue-700'],
                reach: reach.colors['sky-900'],
            }),
        },
        ...Brand.select({
            penji: null,
            reach: {
                secondary: {
                    main: reach.colors['red-400'],
                },
                error: {
                    main: reach.colors['red-400'],
                },
                background: {
                    default: reach.colors['stone-50'],
                },
                text: {
                    primary: reach.colors['stone-950'],
                    secondary: reach.colors['stone-500'],
                },
            },
        }),
    },
    ...Brand.select({
        penji: null,
        reach: {
            typography: getReachTypography(),
            props: {
                MuiAlert: {
                    iconMapping: {
                        error: <SvgIcon component={ReachErrorIcon} />,
                        info: <SvgIcon component={ReachInfoIcon} />,
                        warning: <SvgIcon component={ReachWarningIcon} />,
                    },
                },
                MuiCheckbox: {
                    disableRipple: true,
                    icon: <ReachCheckboxIcon />,
                    checkedIcon: <ReachCheckboxCheckedIcon />,
                    indeterminateIcon: <ReachCheckboxIndeterminateIcon />,
                },
                MuiChip: {
                    disableRipple: true,
                },
                MuiRadio: {
                    disableRipple: true,
                    icon: <ReachRadioIcon />,
                    checkedIcon: <ReachRadioCheckedIcon />,
                },
                MuiTooltip: {
                    arrow: true,
                },
                MuiSelect: {
                    IconComponent: (props) => (
                        <SvgIcon
                            {...props}
                            component={ReachCaretDownBoldIcon}
                        />
                    ),
                },
                MuiAutocomplete: {
                    popupIcon: <SvgIcon component={ReachCaretDownBoldIcon} />,
                    closeIcon: <SvgIcon component={ReachCloseIcon} />,
                },
                MuiButtonBase: { disableRipple: true },
                MuiButtonGroup: { disableRipple: true },
                MuiCardActionArea: { disableRipple: true },
                MuiMenuItem: { disableRipple: true },
                MuiTab: { disableRipple: true },
                MuiCircularProgress: { disableShrink: true },

                MuiPickersCalendar: getReachCalendarArrowIconProps(),
            },
            overrides: {
                MuiAlert: getReachAlertOverrides(),
                MuiButton: getReachButtonOverrides(),
                MuiButtonGroup: getReachButtonGroupOverrides(),
                MuiCheckbox: getReachControlOverrides(),
                MuiChip: getReachChipOverrides(),
                MuiDialog: getReachDialogOverrides(),
                MuiRadio: getReachControlOverrides(),
                MuiFormControlLabel: getReachFormControlLabelOverrides(),
                MuiIconButton: getReachIconButtonOverrides(),
                MuiPopover: getReachPopoverOverrides(),
                MuiPickerDTTabs: {
                    tabs: {
                        '& .Mui-focusVisible': {
                            boxShadow: `inset 0 0 0 2px ${reach.colors['white']}`,
                        },
                    },
                },

                MuiPickersModal: {
                    dialogRoot: {
                        border: 'none',
                    },
                },
                MuiCircularProgress: {
                    colorPrimary: {
                        color: reach.colors['sky-900'],
                    },
                    circle: {
                        strokeLinecap: 'round',
                    },
                    circleIndeterminate: {
                        strokeDasharray: '32px, 128px',
                    },
                },
                MuiPickersToolbarButton: {
                    toolbarBtn: {
                        height: 'auto',
                        '&:hover': {
                            backgroundColor: reach.colors['sky-950'],
                        },
                        '&:focus-visible': {
                            outline: `2px solid ${reach.colors['white']}`,
                        },
                    },
                },
                MuiSvgIcon: {
                    root: {
                        fontSize: 24,
                    },

                    fontSizeSmall: {
                        fontSize: 16,
                    },
                    // Reach doesnot have large sized fonts, default to medium size for now
                    fontSizeLarge: {
                        fontSize: 24,
                    },

                    colorAction: {
                        color: reach.colors['stone-500'],
                    },
                },
                MuiStepIcon: {
                    root: {
                        color: reach.colors['stone-500'],
                    },
                },
                MuiStepLabel: {
                    label: {
                        color: reach.colors['stone-500'],
                    },
                },
                MuiStepConnector: {
                    line: {
                        borderColor: reach.colors['stone-300'],
                    },
                },
                MuiOutlinedInput: getReachOutlinedInputOverrides(),
                MuiAutocomplete: getReachAutocompleteOverrides(),
                MuiSelect: getReachSelectOverrides(),
                MuiFormLabel: getReachFormLabelOverrides(),
                MuiInputLabel: getReachInputLabelOverrides(),
                MuiFormHelperText: getReachFormHelperTextOverrides(),
                MuiPaper: getReachPaperOverrides(),
                MuiDivider: getReachDividerOverrides(),
                MuiMenu: getReachMenuOverrides(),
                MuiMenuItem: getReachMenuItemOverrides(),
                MuiTableCell: getReachTableCellOverrides(),
                MuiTableRow: getReachTableRowOverrides(),
                MuiTooltip: getReachTooltipOverrides(),
                MuiAccordion: getReachAccordionOverrides(),
                MuiAccordionSummary: getReachAccordionSummaryOverrides(),
            },
        },
    }),
});
